import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import clsx from 'clsx';
import {
  getSettings, updateSettings,
  getMembersWithStats, updateMemberRole, approveMember, rejectMember, setMemberActive,
  getMeetings, createMeeting, updateMeeting, deleteMeeting, autoGenerateMeetings,
  getCurrentMember, generateCheckinToken,
  getJoinRequests, approveJoinRequest, rejectJoinRequest,
  getAdminErrors, clearAdminErrors,
  getAppFeedback,
} from '../services/api';
import InviteForm from './InviteForm';
import InvitesList from './InvitesList';
import ConfirmDialog from './ConfirmDialog';
import { FEATURES } from '../config/features';

/* Module scope so currentTab can be normalised against it before render. */
const ADMIN_TABS = [
  { id: 'settings', label: 'Settings' },
  { id: 'members',  label: 'Members' },
  { id: 'meetings', label: 'Meetings', enabled: FEATURES.SCHEDULING },
  { id: 'requests', label: 'Requests' },
  { id: 'errors',   label: 'Processing Issues' },
  { id: 'feedback', label: 'Feedback' },
].filter(t => t.enabled !== false);

/* ── Shared style tokens ── */
const S = {
  label:     { color: 'var(--text-faint)', fontSize: '13px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: '6px' },
  card:      { background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '10px' },
  cardWarn:  { background: 'var(--color-warning-dim)', border: '1px solid var(--color-warning-border)', borderRadius: '10px' },
  divider:   { borderTop: '1px solid var(--border-subtle)', paddingTop: '24px', marginTop: '8px' },
  sectionHd: { color: 'var(--text-faint)', fontSize: '13px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '16px' },
  muted:     { color: 'var(--text-muted)' },
  dimmer:    { color: 'var(--text-faint)' },
  danger:    { background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger)', borderRadius: '8px' },
  success:   { background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)', color: 'var(--color-success)', borderRadius: '8px' },
};

const ADMIN_PAGE_SIZE = 50;

function Paginator({ page, totalPages, onPrev, onNext }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-4 pt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
      <button
        onClick={onPrev}
        disabled={page === 0}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-30 transition-colors"
        style={{ color: 'var(--text-secondary)', background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}
      >
        ← Prev
      </button>
      <span className="text-xs font-mono" style={{ color: 'var(--text-faint)' }}>
        {page + 1} / {totalPages}
      </span>
      <button
        onClick={onNext}
        disabled={page === totalPages - 1}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-30 transition-colors"
        style={{ color: 'var(--text-secondary)', background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}
      >
        Next →
      </button>
    </div>
  );
}

function MsgBanner({ msg }) {
  if (!msg) return null;
  return (
    <div className="px-4 py-2 text-sm font-medium" style={msg.type === 'success' ? S.success : S.danger}>
      {msg.text}
    </div>
  );
}

function DangerBtn({ onClick, disabled, children, className = '' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx('text-sm px-3 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-40', className)}
      style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger)' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--color-danger-dim)'}
      onMouseLeave={e => e.currentTarget.style.background = 'var(--color-danger-dim)'}
    >
      {children}
    </button>
  );
}

function GhostBtn({ onClick, disabled, children, className = '' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx('text-sm px-3 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-40', className)}
      style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-surface)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-input)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
    >
      {children}
    </button>
  );
}

export default function AdminPanel() {
  usePageTitle('Admin');
  const [searchParams, setSearchParams] = useSearchParams();
  const [, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const fetchedRef = useRef(false);

  // Normalise: a bookmarked ?tab=meetings must not render a hidden tab, and an
  // unknown tab previously rendered a blank pane. Both fall back to Settings.
  const requestedTab = searchParams.get('tab') || 'settings';
  const currentTab = ADMIN_TABS.some(t => t.id === requestedTab) ? requestedTab : 'settings';

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMember = async () => {
    try {
      const res = await getCurrentMember();
      if (res?.member) {
        if (res.member.role !== 'admin') setError('Access denied. Admin role required.');
        else setMember(res.member);
      }
    } catch (err) {
      setError(err.message || 'Failed to verify admin access');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequestCount = async () => {
    try {
      const res = await getJoinRequests({ status: 'pending' });
      setPendingRequestCount(res?.requests?.length || 0);
    } catch (_) {}
  };

  useEffect(() => {
    loadPendingRequestCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTab = (tab) => setSearchParams({ tab });

  if (loading) return (
    <div className="text-center py-16 text-sm" style={S.muted}>Verifying admin access…</div>
  );
  if (error) return (
    <div className="text-center py-16">
      <p className="text-2xl mb-3 opacity-30">🔒</p>
      <p className="font-medium text-sm" style={S.muted}>{error}</p>
      <Link to="/" className="inline-block mt-4 text-sm font-medium transition-colors" style={{ color: 'var(--accent)' }}>← Back to Dashboard</Link>
    </div>
  );

  // Badge depends on component state, so it's attached here rather than in the
  // module-scope ADMIN_TABS registry.
  const tabs = ADMIN_TABS.map(t =>
    t.id === 'requests' ? { ...t, badge: pendingRequestCount } : t
  );

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-1 flex-wrap pb-1" style={{ borderBottom: '1px solid var(--border-accent)' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setTab(tab.id)}
            className={clsx('pill-tab text-sm flex items-center gap-1.5', currentTab === tab.id ? 'active' : '')}
          >
            {tab.label}
            {tab.badge != null && (
              <span
                className="px-1.5 py-0.5 rounded-full text-xs font-bold leading-none"
                style={{
                  background: tab.badge > 0 ? 'var(--color-danger)' : 'var(--accent-dim)',
                  color: tab.badge > 0 ? '#fff' : 'var(--text-faint)',
                }}
              >
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {currentTab === 'settings' && <SettingsTab />}
      {currentTab === 'members'  && <MembersTab />}
      {/* Keep this line even when hidden: MeetingsTab must stay referenced or
          CI=true builds fail on no-unused-vars. */}
      {currentTab === 'meetings' && FEATURES.SCHEDULING && <MeetingsTab />}
      {currentTab === 'requests' && <RequestsTab onCountChange={setPendingRequestCount} />}
      {currentTab === 'errors'   && <ErrorsTab />}
      {currentTab === 'feedback' && <AppFeedbackTab />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Settings Tab
// ══════════════════════════════════════════════════════════════════════════

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getSettings();
      if (res?.settings) setSettings(res.settings);
    } catch (err) {
      console.error('[Settings] Load error:', err);
      setMessage({ type: 'error', text: 'Failed to load settings. Refresh before saving.' });
    } finally {
      setLoading(false);
    }
  };

  const days = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

  const validateSettings = (s) => {
    if (s.meeting_day && !days.includes(s.meeting_day.toLowerCase())) return "That's not a valid day of the week";
    if (s.meeting_time && !/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(s.meeting_time)) return 'Time must be HH:MM (e.g. 18:30)';
    if (s.slots_per_meeting !== undefined) {
      const n = parseInt(s.slots_per_meeting, 10);
      if (isNaN(n) || n < 1 || n > 10) return 'Between 1 and 10 slots, please';
    }
    if (s.club_name && s.club_name.length > 100) return 'Club name is too long (max 100 characters)';
    if (s.meeting_number_seed !== undefined) {
      const seed = parseInt(s.meeting_number_seed, 10);
      if (isNaN(seed) || seed < 1) return 'Enter a positive number (e.g. 1)';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    const err = validateSettings(settings);
    if (err) { setMessage({ type: 'error', text: err }); setSaving(false); return; }
    try {
      await updateSettings(settings);
      setMessage({ type: 'success', text: 'Settings saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm py-8 text-center" style={S.muted}>Loading settings…</div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-xl">
      <p style={S.sectionHd}>Club Settings</p>

      {/* Club Identity */}
      <div style={S.divider}>
        <p style={S.sectionHd}>Club Identity</p>
        <div className={FEATURES.SCHEDULING ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : 'grid grid-cols-1 gap-4'}>
          <div>
            <label htmlFor="setting-club-name" style={S.label}>Club Name</label>
            <input
              id="setting-club-name"
              type="text"
              maxLength={100}
              value={settings?.club_name || ''}
              onChange={(e) => setSettings(s => ({ ...s, club_name: e.target.value || null }))}
              placeholder="Larynx"
              className="w-full px-3 py-2 text-sm"
            />
            <p className="text-xs mt-1.5" style={S.dimmer}>Used in auto-generated meeting names, e.g. "Larynx 301"</p>
          </div>
          {/* NOTE: scheduling/gamification inputs below are hidden, but their
              values stay in the `settings` object so handleSubmit round-trips
              them unchanged. Never strip them — updateSettings would then reset
              them to service defaults, which is silent data loss. */}
          {FEATURES.SCHEDULING && (
            <div>
              <label htmlFor="setting-meeting-number" style={S.label}>Next Meeting Number</label>
              <input
                id="setting-meeting-number"
                type="number" min="1"
                value={settings?.meeting_number_seed ?? 1}
                onChange={(e) => setSettings(s => ({ ...s, meeting_number_seed: parseInt(e.target.value) || 1 }))}
                className="w-full px-3 py-2 text-sm"
              />
              <p className="text-xs mt-1.5" style={S.dimmer}>Auto-incremented for each new meeting (e.g., Larynx 316)</p>
            </div>
          )}
        </div>
      </div>

      {/* Meeting Day */}
      {FEATURES.SCHEDULING && (
      <div>
        <label htmlFor="setting-meeting-day" style={S.label}>Weekly Meeting Day</label>
        <select
          id="setting-meeting-day"
          value={settings?.meeting_day || 'saturday'}
          onChange={(e) => setSettings(s => ({ ...s, meeting_day: e.target.value }))}
          className="w-full px-3 py-2 text-sm"
        >
          {days.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
        </select>
      </div>
      )}

      {/* Meeting Time */}
      {FEATURES.SCHEDULING && (
      <div>
        <label htmlFor="setting-meeting-time" style={S.label}>Meeting Time</label>
        <input
          id="setting-meeting-time"
          type="time"
          value={settings?.meeting_time || '10:00'}
          onChange={(e) => setSettings(s => ({ ...s, meeting_time: e.target.value }))}
          className="w-full px-3 py-2 text-sm"
        />
      </div>
      )}

      {/* Auto-approve Domain */}
      <div>
        <label htmlFor="setting-auto-approve" style={S.label}>Auto-approve Email Domain</label>
        <input
          id="setting-auto-approve"
          type="text"
          value={settings?.auto_approve_domain || ''}
          onChange={(e) => setSettings(s => ({ ...s, auto_approve_domain: e.target.value }))}
          placeholder="@zohocorp.com"
          className="w-full px-3 py-2 text-sm"
        />
        <p className="text-xs mt-1.5" style={S.dimmer}>Users with this domain are approved automatically.</p>
      </div>

      {/* Slots per Meeting */}
      {FEATURES.SCHEDULING && (
      <div>
        <label htmlFor="setting-slots" style={S.label}>Speech Slots per Meeting</label>
        <input
          id="setting-slots"
          type="number" min="1" max="10"
          value={settings?.slots_per_meeting || 2}
          onChange={(e) => setSettings(s => ({ ...s, slots_per_meeting: parseInt(e.target.value) }))}
          className="w-full px-3 py-2 text-sm"
        />
      </div>
      )}

      {/* Gamification */}
      {FEATURES.GAMIFICATION && (
      <div style={S.divider}>
        <p style={S.sectionHd}>Gamification Points</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="setting-points-attendance" style={S.label}>Per Attendance</label>
            <input
              id="setting-points-attendance"
              type="number" min="0" max="1000"
              value={settings?.points_attendance ?? 10}
              onChange={(e) => setSettings(s => ({ ...s, points_attendance: parseInt(e.target.value) || 0 }))}
              className="w-full px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="setting-points-speech" style={S.label}>Per Speech</label>
            <input
              id="setting-points-speech"
              type="number" min="0" max="1000"
              value={settings?.points_speech ?? 50}
              onChange={(e) => setSettings(s => ({ ...s, points_speech: parseInt(e.target.value) || 0 }))}
              className="w-full px-3 py-2 text-sm"
            />
          </div>
        </div>
        <p className="text-xs mt-2" style={S.dimmer}>Points shown on the leaderboard.</p>
      </div>
      )}

      {/* AI Prompt */}
      <div style={S.divider}>
        <p style={S.sectionHd}>AI Coaching Prompt</p>
        <p className="text-xs mb-3" style={S.dimmer}>
          Custom instructions for the AI coach. Leave empty to use the default.
        </p>
        <label htmlFor="setting-coaching-prompt" className="sr-only">AI coaching prompt</label>
        <textarea
          id="setting-coaching-prompt"
          value={settings?.coaching_prompt || ''}
          onChange={(e) => setSettings(s => ({ ...s, coaching_prompt: e.target.value || null }))}
          placeholder="e.g. You are a public speaking coach. Focus on clarity and confident delivery…"
          rows={6}
          maxLength={9000}
          className="w-full px-3 py-2 text-sm font-mono resize-y"
        />
        <p className="text-xs mt-1" style={S.dimmer}>{(settings?.coaching_prompt || '').length}/9000</p>
      </div>

      <MsgBanner msg={message} />

      <button type="submit" disabled={saving} className="btn-bouncy text-sm px-6 py-2 font-semibold">
        {saving ? 'Saving…' : 'Save Settings'}
      </button>
    </form>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Members Tab
// ══════════════════════════════════════════════════════════════════════════

const SORT_OPTIONS = [
  { value: 'joined_desc', label: 'Joined (newest)' },
  { value: 'joined_asc',  label: 'Joined (oldest)' },
  { value: 'seen_desc',   label: 'Last seen (recent)' },
  { value: 'seen_asc',    label: 'Last seen (oldest)' },
];

function formatRelativeTime(ts) {
  if (!ts) return null;
  const diff = Date.now() - Number(ts);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(Number(ts)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function MembersTab() {
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('joined_desc');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState(null);
  const [inviteRefresh, setInviteRefresh] = useState(0);
  const [pendingConfirm, setPendingConfirm] = useState(null);

  useEffect(() => { setPage(0); loadMembers(); }, [filter]); // eslint-disable-line

  const loadMembers = async () => {
    setLoading(true);
    try {
      const params = { includeInactive: true };
      if (filter === 'pending') params.status = 'pending';
      if (filter === 'approved') params.status = 'approved';
      const res = await getMembersWithStats(params);
      if (res?.members) setMembers(res.members);
    } catch (err) {
      console.error('[Members] Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove     = async (id) => { setActionError(null); try { await approveMember(id); loadMembers(); } catch (e) { setActionError(e.message); } };
  const handleReject       = (id) => { setPendingConfirm({ message: 'Reject this member?', confirmLabel: 'Reject', variant: 'danger', onConfirm: async () => { setActionError(null); try { await rejectMember(id); loadMembers(); } catch (e) { setActionError(e.message); } } }); };
  const handleRoleChange   = async (id, role) => { setActionError(null); try { await updateMemberRole(id, role); loadMembers(); } catch (e) { setActionError(e.message); } };
  const handleToggleActive = async (id, active) => { setActionError(null); try { await setMemberActive(id, !active); loadMembers(); } catch (e) { setActionError(e.message); } };

  const pendingCount  = members.filter(m => m.status === 'pending').length;
  const approvedCount = members.filter(m => m.status === 'approved').length;

  // Search + sort (client-side)
  const q = search.toLowerCase().trim();
  // Deduplicate by member_id (DataStore may have duplicate rows)
  const seen = new Set();
  const deduped = members.filter(m => { const id = String(m.member_id); if (seen.has(id)) return false; seen.add(id); return true; });
  // Word-start matching: each query token must start a word in name/email
  const matchesMember = (m) => {
    if (!q) return true;
    const haystack = `${m.first_name || ''} ${m.last_name || ''} ${m.email || ''}`.toLowerCase();
    return q.split(/\s+/).every(token => new RegExp(`(^|\\s)${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(haystack));
  };
  const filtered = deduped
    .filter(matchesMember)
    .sort((a, b) => {
      if (sort === 'joined_desc') return Number(b.joined_at || 0) - Number(a.joined_at || 0);
      if (sort === 'joined_asc')  return Number(a.joined_at || 0) - Number(b.joined_at || 0);
      if (sort === 'seen_desc')   return Number(b.last_seen_at || 0) - Number(a.last_seen_at || 0);
      if (sort === 'seen_asc')    return Number(a.last_seen_at || 0) - Number(b.last_seen_at || 0);
      return 0;
    });

  const memberPages = Math.ceil(filtered.length / ADMIN_PAGE_SIZE);
  const pagedMembers = filtered.slice(page * ADMIN_PAGE_SIZE, (page + 1) * ADMIN_PAGE_SIZE);

  return (
    <div className="space-y-8">
      {/* Invite */}
      <div className="rounded-xl p-5" style={{ background: 'var(--color-info-dim)', border: '1px solid var(--color-info-border)' }}>
        <p className="text-xs font-semibold mb-3" style={{ color: 'var(--color-info)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Invite New Member</p>
        <InviteForm onInviteSent={() => setInviteRefresh(v => v + 1)} />
      </div>

      <InvitesList refreshTrigger={inviteRefresh} />

      {actionError && <div className="px-4 py-2 text-sm font-medium rounded-lg" style={S.danger}>{actionError}</div>}

      {/* Members list */}
      <div className="space-y-4">
        {/* Header row */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <p style={{ ...S.sectionHd, marginBottom: 0 }}>Members</p>
            <span className="font-mono text-xs" style={{ color: 'var(--text-faint)' }}>{approvedCount} joined</span>
            {pendingCount > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'var(--color-warning-dim)', color: 'var(--color-warning)', border: '1px solid var(--color-warning-border)' }}>
                {pendingCount} pending
              </span>
            )}
          </div>
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-accent)' }}>
            {['all','pending','approved'].map(f => (
              <button key={f} onClick={() => { setFilter(f); setPage(0); }} className={clsx('pill-tab text-xs capitalize', filter === f ? 'active' : '')}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Search + sort controls */}
        <div className="flex gap-2">
          <label htmlFor="members-search" className="sr-only">Search members</label>
          <input
            id="members-search"
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            className="flex-1 text-sm rounded-lg px-3 py-2 outline-none"
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}
          />
          <select
            value={sort}
            onChange={e => { setSort(e.target.value); setPage(0); }}
            className="text-xs rounded-lg px-2 py-2 flex-shrink-0"
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)', minWidth: 160 }}
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="text-sm py-8 text-center" style={S.muted}>Loading members…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl p-8 text-center" style={{ background: 'var(--bg-input)', border: '1px dashed var(--border-accent)' }}>
            <p className="text-sm" style={S.muted}>{search ? `No members match "${search}".` : 'No members found.'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {pagedMembers.map((m) => (
              <MemberRow
                key={m.member_id}
                member={m}
                onApprove={() => handleApprove(m.member_id)}
                onReject={() => handleReject(m.member_id)}
                onRoleChange={(role) => handleRoleChange(m.member_id, role)}
                onToggleActive={() => handleToggleActive(m.member_id, m.is_active !== false)}
              />
            ))}
            {q && (
              <p className="text-xs text-center pt-1 font-mono" style={{ color: 'var(--text-faint)' }}>
                {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"
              </p>
            )}
            <Paginator page={page} totalPages={memberPages} onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
          </div>
        )}
      </div>

      {pendingConfirm && (
        <ConfirmDialog
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          variant={pendingConfirm.variant}
          onConfirm={() => { pendingConfirm.onConfirm(); setPendingConfirm(null); }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}

function MemberRow({ member, onApprove, onReject, onRoleChange, onToggleActive }) {
  const { email, first_name, last_name, role, status, is_active, joined_at, last_seen_at,
          attendance_count = 0, speech_count = 0 } = member;
  const roles = ['member','evaluator','admin'];
  const isPending = status === 'pending';
  const isInactive = is_active === false || is_active === 'false';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const initial = ((first_name?.[0] || '') + (last_name?.[0] || '')).toUpperCase() || email?.[0]?.toUpperCase() || '?';

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const roleBadgeStyle = {
    admin:     { background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--border-accent)' },
    evaluator: { background: 'var(--accent-dim)',  color: 'var(--accent)', border: '1px solid var(--border-accent)' },
    member:    { background: 'var(--bg-input)',         color: 'var(--text-faint)', border: '1px solid var(--border-subtle)' },
  }[role] || {};

  return (
    <div
      className="p-4 flex items-center justify-between gap-4"
      style={Object.assign({}, isPending ? S.cardWarn : S.card, { opacity: isInactive ? 0.55 : 1 })}
    >
      {/* Left: avatar + info */}
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
          style={{ background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--border-accent)' }}
        >
          {initial}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              {first_name} {last_name}
            </p>
            <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={roleBadgeStyle}>
              {role}
            </span>
            {isInactive && <span className="text-xs font-semibold" style={{ color: 'var(--color-danger)' }}>Inactive</span>}
          </div>
          <p className="text-xs mt-0.5 truncate" style={S.muted}>{email}</p>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-xs" style={S.dimmer}>
              Joined {joined_at ? new Date(Number(joined_at)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
            </span>
            {last_seen_at && (
              <span className="text-xs" style={S.dimmer}>
                · Last seen {formatRelativeTime(last_seen_at)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Right: stats + actions */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Stats (approved members only) */}
        {!isPending && (
          <div className="hidden sm:flex items-center gap-3 text-xs font-mono" style={{ color: 'var(--text-faint)' }}>
            {FEATURES.GAMIFICATION && <span>{attendance_count} attended</span>}
            <span>{speech_count} {speech_count === 1 ? 'speech' : 'speeches'}</span>
          </div>
        )}

        {isPending ? (
          <div className="flex gap-2">
            <button onClick={onApprove} className="btn-bouncy text-xs px-3 py-2">Approve</button>
            <DangerBtn onClick={onReject}>Reject</DangerBtn>
          </div>
        ) : (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(v => !v)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors text-base"
              style={{ background: menuOpen ? 'var(--bg-input)' : 'transparent', color: 'var(--text-faint)', border: '1px solid transparent' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-input)'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
              onMouseLeave={e => { if (!menuOpen) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; } }}
              title="Member actions"
              aria-label={`Member actions for ${first_name || ''} ${last_name || ''}`.trim() || `Member actions for ${email}`}
              aria-expanded={menuOpen}
              aria-haspopup="true"
            >
              ⋯
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-1 rounded-xl shadow-lg z-20 min-w-48 py-1"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)', top: '100%' }}
              >
                <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-faint)' }}>Change role</p>
                  <div className="flex flex-col gap-1">
                    {roles.map(r => (
                      <button
                        key={r}
                        onClick={() => { onRoleChange(r); setMenuOpen(false); }}
                        className="text-left text-xs px-2 py-1.5 rounded-lg capitalize transition-colors"
                        style={{
                          background: role === r ? 'var(--accent-dim)' : 'transparent',
                          color: role === r ? 'var(--accent)' : 'var(--text-secondary)',
                          fontWeight: role === r ? 600 : 400,
                        }}
                        onMouseEnter={e => { if (role !== r) e.currentTarget.style.background = 'var(--bg-input)'; }}
                        onMouseLeave={e => { if (role !== r) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                        {role === r && ' ✓'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="px-2 py-1">
                  {isInactive ? (
                    <button
                      onClick={() => { onToggleActive(); setMenuOpen(false); }}
                      className="w-full text-left text-xs px-2 py-1.5 rounded-lg transition-colors"
                      style={{ color: 'var(--color-success)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-success-dim)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      Reactivate member
                    </button>
                  ) : (
                    <button
                      onClick={() => { onToggleActive(); setMenuOpen(false); }}
                      className="w-full text-left text-xs px-2 py-1.5 rounded-lg transition-colors"
                      style={{ color: 'var(--color-danger)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-danger-dim)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      Deactivate member
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Meetings Tab
// ══════════════════════════════════════════════════════════════════════════

function MeetingsTab() {
  const [meetings, setMeetings] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [qrModal, setQrModal] = useState(null);
  const [qrLoading, setQrLoading] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [editMeetingId, setEditMeetingId] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', title: '' });
  const [editSaving, setEditSaving] = useState(false);
  const fetchedRef = useRef(false);

  const todayStr = new Date().toISOString().split('T')[0];
  const minDate = new Date();
  minDate.setDate(minDate.getDate() - 90);
  const minDateStr = minDate.toISOString().split('T')[0];

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadMeetings();
  }, []); // eslint-disable-line

  const loadMeetings = async () => {
    setLoading(true);
    try {
      const res = await getMeetings('all', 200);
      if (res?.meetings) { setMeetings(res.meetings); setPage(0); }
    } catch (err) { console.error('[Meetings] Load error:', err); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newDate) return;
    setCreating(true); setMessage(null);
    try {
      const numOverride = newNumber ? parseInt(newNumber, 10) : undefined;
      const res = await createMeeting(newDate, newTitle || undefined, numOverride);
      const createdId = res?.meeting?.meeting_id;
      setNewDate(''); setNewTitle(''); setNewNumber('');
      setMessage({ type: 'success', text: 'Meeting created.', meetingId: createdId });
      loadMeetings();
    } catch (err) { setMessage({ type: 'error', text: err.message || 'Failed to create' }); }
    finally { setCreating(false); }
  };

  const handleAutoGenerate = () => {
    setPendingConfirm({
      message: 'Auto-generate meetings for the next 4 weeks?',
      confirmLabel: 'Generate',
      onConfirm: async () => {
        setMessage(null);
        try {
          const res = await autoGenerateMeetings(4);
          setMessage({ type: 'success', text: `Created ${res.created} meetings` });
          loadMeetings();
        } catch (err) { setMessage({ type: 'error', text: err.message || 'Failed to generate' }); }
      },
    });
  };

  const handleCancel = (id) => {
    setPendingConfirm({
      message: 'Cancel this meeting?',
      confirmLabel: 'Cancel meeting',
      variant: 'danger',
      onConfirm: async () => {
        setMessage(null);
        try { await updateMeeting(id, { status: 'cancelled' }); loadMeetings(); } // eslint-disable-line
        catch (err) { setMessage({ type: 'error', text: err.message || 'Failed to cancel' }); }
      },
    });
  };

  const handleDeleteMeeting = (m) => {
    const slotCount = m.slots?.length || 0;
    const speechCount = m.slots?.filter(s => s.status === 'completed').length || 0;
    const detail = speechCount > 0
      ? `This will permanently delete ${slotCount} slot${slotCount !== 1 ? 's' : ''}, ${speechCount} speech${speechCount !== 1 ? 'es' : ''}, and all associated comments.`
      : `This will permanently delete ${slotCount} slot${slotCount !== 1 ? 's' : ''} and attendance records.`;
    setPendingConfirm({
      message: `Delete "${m.title || 'this meeting'}"? ${detail} This cannot be undone.`,
      confirmLabel: 'Delete meeting',
      variant: 'danger',
      onConfirm: async () => {
        setMessage(null);
        try { await deleteMeeting(m.meeting_id); loadMeetings(); }
        catch (err) { setMessage({ type: 'error', text: err.message || 'Failed to delete meeting' }); }
      },
    });
  };

  const openEditMeeting = (m) => {
    setEditMeetingId(m.meeting_id);
    setEditForm({ date: m.meeting_date, title: m.title || '' });
    setMessage(null);
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    setMessage(null);
    try {
      await updateMeeting(editMeetingId, {
        meeting_date: editForm.date || undefined,
        title: editForm.title || undefined,
      });
      setEditMeetingId(null);
      loadMeetings();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save' });
    } finally {
      setEditSaving(false);
    }
  };

  const handleGenerateQR = async (id) => {
    setQrLoading(id); setMessage(null);
    try {
      const res = await generateCheckinToken(id);
      setQrModal({ qrDataUrl: res.qrDataUrl, checkinUrl: res.checkinUrl, expiresAt: res.expiresAt });
    } catch (err) { setMessage({ type: 'error', text: err.message || 'Failed to generate QR' }); }
    finally { setQrLoading(null); }
  };

  const meetingPages = Math.ceil(meetings.length / ADMIN_PAGE_SIZE);
  const pagedMeetings = meetings.slice(page * ADMIN_PAGE_SIZE, (page + 1) * ADMIN_PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p style={S.sectionHd} className="mb-0">All Meetings</p>
        <GhostBtn onClick={handleAutoGenerate}>Auto-Generate 4 Weeks</GhostBtn>
      </div>

      {/* Create Meeting form */}
      <form
        onSubmit={handleCreate}
        className="rounded-xl p-4 flex flex-col gap-3 sm:flex-row sm:items-start"
        style={{ background: 'var(--bg-input)', border: '1px solid var(--border-accent)' }}
      >
        <div className="flex-1">
          <label style={S.label}>
            Date
            {newDate && newDate < todayStr && (
              <span className="ml-1.5 text-xs rounded-full px-2 py-0.5 font-normal"
                style={{ background: 'var(--color-warning-dim)', color: 'var(--color-warning)', textTransform: 'none', letterSpacing: 0 }}>
                will be completed
              </span>
            )}
          </label>
          <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="w-full px-3 py-2 text-sm" min={minDateStr} required />
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Past dates up to 3 months allowed</p>
        </div>
        <div className="flex-1">
          <label style={S.label}>Title (optional)</label>
          <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Auto from club name" className="w-full px-3 py-2 text-sm" />
        </div>
        <div className="w-full sm:w-[6.5rem]">
          <label style={S.label}># (optional)</label>
          <input type="number" min="1" value={newNumber} onChange={(e) => setNewNumber(e.target.value)} placeholder="300" className="w-full px-3 py-2 text-sm" />
        </div>
        <button type="submit" disabled={creating || !newDate} className="btn-bouncy text-sm px-4 py-2 font-semibold w-full sm:w-auto sm:flex-shrink-0">
          {creating ? 'Creating…' : 'Create'}
        </button>
      </form>

      {message && (
        <div className="px-4 py-2 text-sm font-medium flex items-center gap-3" style={message.type === 'success' ? S.success : S.danger}>
          <span>{message.text}</span>
          {message.meetingId && (
            <Link
              to={`/meetings/${message.meetingId}`}
              className="font-semibold underline underline-offset-2 flex-shrink-0"
              style={{ color: 'var(--color-success)' }}
            >
              Manage →
            </Link>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-sm py-8 text-center" style={S.muted}>Loading meetings…</div>
      ) : meetings.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ background: 'var(--bg-input)', border: '1px dashed var(--border-accent)' }}>
          <p className="text-sm" style={S.muted}>No meetings yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {pagedMeetings.map((m) => (
            <div
              key={m.meeting_id}
              className="rounded-xl overflow-hidden"
              style={Object.assign({}, S.card, m.status === 'cancelled' ? { opacity: 0.5, borderColor: 'var(--color-danger-border)' } : {})}
            >
              {/* Meeting row */}
              <div className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-semibold text-sm flex items-center gap-2 flex-wrap" style={{ color: 'var(--text-primary)' }}>
                    {m.title}
                    {m.meeting_number && (
                      <span className="text-xs font-bold tracking-widest px-1.5 py-0.5 rounded"
                        style={{ background: 'var(--accent-dim)', color: 'var(--accent)' }}>
                        #{m.meeting_number}
                      </span>
                    )}
                    {m.status === 'cancelled' && <span className="text-xs font-normal" style={{ color: 'var(--color-danger)' }}>(Cancelled)</span>}
                  </p>
                  <p className="text-xs mt-0.5" style={S.muted}>
                    {new Date(m.meeting_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </p>
                  <p className="text-xs mt-0.5" style={S.dimmer}>
                    {m.slots?.filter(s => s.status !== 'open').length || 0}/{m.slots?.length || 0} slots booked
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap sm:flex-shrink-0">
                  {m.status !== 'cancelled' && editMeetingId !== m.meeting_id && (
                    <button
                      onClick={() => openEditMeeting(m)}
                      className="text-xs font-semibold transition-colors"
                      style={{ color: 'var(--text-faint)' }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}
                    >
                      Edit
                    </button>
                  )}
                  {editMeetingId === m.meeting_id && (
                    <button
                      onClick={() => setEditMeetingId(null)}
                      className="text-xs font-semibold transition-colors"
                      style={{ color: 'var(--text-faint)' }}
                    >
                      ✕
                    </button>
                  )}
                  {m.status === 'scheduled' && (
                    <>
                      <button
                        onClick={() => handleGenerateQR(m.meeting_id)}
                        disabled={qrLoading === m.meeting_id}
                        className="text-xs font-semibold transition-colors disabled:opacity-40"
                        style={{ color: 'var(--color-info)' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--color-info)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--color-info)'}
                      >
                        {qrLoading === m.meeting_id ? '…' : 'QR'}
                      </button>
                      <button
                        onClick={() => handleCancel(m.meeting_id)}
                        className="text-xs font-semibold transition-colors"
                        style={{ color: 'var(--color-danger)' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--color-danger)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--color-danger)'}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDeleteMeeting(m)}
                    className="text-xs font-semibold transition-colors"
                    style={{ color: 'var(--color-danger)', opacity: 0.7 }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                  >
                    Delete
                  </button>
                  <Link
                    to={`/meetings/${m.meeting_id}`}
                    className="text-xs font-semibold transition-colors"
                    style={{ color: 'var(--text-secondary)' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                  >
                    View →
                  </Link>
                </div>
              </div>

              {/* Inline edit form */}
              {editMeetingId === m.meeting_id && (
                <div
                  className="px-4 pb-4 pt-0 flex gap-3 items-end flex-wrap"
                  style={{ borderTop: '1px solid var(--border-subtle)' }}
                >
                  <div>
                    <label style={{ ...S.label, marginBottom: 4 }}>Date</label>
                    <input
                      type="date"
                      value={editForm.date}
                      min={minDateStr}
                      onChange={(e) => setEditForm(f => ({ ...f, date: e.target.value }))}
                      className="px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div className="flex-1" style={{ minWidth: '10rem' }}>
                    <label style={{ ...S.label, marginBottom: 4 }}>Title</label>
                    <input
                      type="text"
                      value={editForm.title}
                      placeholder="Meeting title"
                      onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
                      className="w-full px-3 py-1.5 text-sm"
                    />
                  </div>
                  <button
                    onClick={handleSaveEdit}
                    disabled={editSaving || !editForm.date}
                    className="btn-bouncy text-sm px-4 py-2 disabled:opacity-50"
                  >
                    {editSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          ))}
          <Paginator page={page} totalPages={meetingPages} onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
        </div>
      )}

      {/* QR Modal */}
      {qrModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-2xl max-w-sm w-full p-6" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}>
            <div className="flex items-center justify-between mb-5">
              <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Check-in QR Code</p>
              <button onClick={() => setQrModal(null)} className="text-xl leading-none transition-colors" style={{ color: 'var(--text-faint)' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}>×</button>
            </div>
            <div className="text-center">
              <img src={qrModal.qrDataUrl} alt="Check-in QR Code" className="mx-auto mb-4 rounded-xl" style={{ width: 260, height: 260 }} />
              <p className="text-xs mb-2" style={S.muted}>Display at the meeting for members to scan</p>
              {qrModal.expiresAt && (
                <p className="text-xs mb-4" style={S.dimmer}>
                  Expires: {new Date(Number(qrModal.expiresAt)).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              <GhostBtn onClick={() => navigator.clipboard.writeText(qrModal.checkinUrl)}>Copy Link</GhostBtn>
            </div>
          </div>
        </div>
      )}

      {pendingConfirm && (
        <ConfirmDialog
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          variant={pendingConfirm.variant}
          onConfirm={() => { pendingConfirm.onConfirm(); setPendingConfirm(null); }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Requests Tab
// ══════════════════════════════════════════════════════════════════════════

function RequestsTab({ onCountChange }) {
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState(null);
  const [approvedInvitations, setApprovedInvitations] = useState({});
  const [pendingConfirm, setPendingConfirm] = useState(null);

  useEffect(() => { loadRequests(); }, []); // eslint-disable-line

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await getJoinRequests({ status: 'pending' });
      const list = res?.requests || [];
      setRequests(list);
      onCountChange(list.length);
    } catch (err) { console.error('[RequestsTab]', err); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    setActionError(null);
    try {
      const res = await approveJoinRequest(id);
      setApprovedInvitations(p => ({ ...p, [id]: res.invitation }));
      setRequests(p => p.filter(r => r.request_id !== id));
      onCountChange(c => Math.max(0, c - 1));
    } catch (e) { setActionError(e.message || 'Failed to approve'); }
  };

  const handleReject = (id) => {
    setPendingConfirm({
      message: 'Reject this join request?',
      confirmLabel: 'Reject',
      variant: 'danger',
      onConfirm: async () => {
        setActionError(null);
        try {
          await rejectJoinRequest(id);
          setRequests(p => p.filter(r => r.request_id !== id));
          onCountChange(c => Math.max(0, c - 1));
        } catch (e) { setActionError(e.message || 'Failed to reject'); }
      },
    });
  };

  const requestPages = Math.ceil(requests.length / ADMIN_PAGE_SIZE);
  const pagedRequests = requests.slice(page * ADMIN_PAGE_SIZE, (page + 1) * ADMIN_PAGE_SIZE);

  if (loading) return <div className="text-sm py-8 text-center" style={S.muted}>Loading requests…</div>;

  return (
    <div className="space-y-4">
      <p style={S.sectionHd} className="mb-1">Join Requests</p>
      <p className="text-sm" style={S.muted}>
        These users signed in but aren't club members yet. Approve to send an invitation link.
      </p>

      {actionError && <div className="px-4 py-2 text-sm rounded-lg" style={S.danger}>{actionError}</div>}

      {/* Generated invitation links */}
      {Object.entries(approvedInvitations).map(([reqId, inv]) => (
        <div key={reqId} className="rounded-xl p-4 text-sm" style={{ background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)' }}>
          <p className="font-semibold mb-2" style={{ color: 'var(--color-success)' }}>Invite link for {inv.email}:</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <code
              className="flex-1 min-w-0 text-xs px-3 py-1.5 rounded-lg truncate"
              style={{ background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)', color: 'var(--text-secondary)' }}
            >
              {window.location.origin}/#/join?token={inv.token}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/#/join?token=${inv.token}`)}
              className="text-xs px-3 py-1.5 rounded-lg font-semibold whitespace-nowrap w-full sm:w-auto flex-shrink-0"
              style={{ background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)', color: 'var(--color-success)' }}
            >
              Copy
            </button>
          </div>
        </div>
      ))}

      {requests.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ background: 'var(--bg-input)', border: '1px dashed var(--border-accent)' }}>
          <p className="text-sm" style={S.muted}>No pending requests.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {pagedRequests.map((r) => (
            <div key={r.request_id} className="p-4" style={S.cardWarn}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm break-words" style={{ color: 'var(--text-primary)' }}>{r.name}</p>
                  <p className="text-xs mt-0.5 break-all" style={S.muted}>{r.email}</p>
                  <p className="text-xs mt-0.5" style={S.dimmer}>
                    {r.created_at ? new Date(Number(r.created_at)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </p>
                  {r.reason && (
                    <p className="mt-2 text-sm px-3 py-2 rounded-lg" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)' }}>
                      {r.reason}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button onClick={() => handleApprove(r.request_id)} className="btn-bouncy text-xs px-3 py-2">Approve</button>
                  <DangerBtn onClick={() => handleReject(r.request_id)}>Reject</DangerBtn>
                </div>
              </div>
            </div>
          ))}
          <Paginator page={page} totalPages={requestPages} onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
        </div>
      )}
      {pendingConfirm && (
        <ConfirmDialog
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          variant={pendingConfirm.variant}
          onConfirm={() => { pendingConfirm.onConfirm(); setPendingConfirm(null); }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Errors Tab
// ══════════════════════════════════════════════════════════════════════════

const SEVERITY = {
  critical: { bg: 'var(--color-danger-dim)',   color: 'var(--color-danger)'  },
  error:    { bg: 'var(--accent-dim)',         color: 'var(--accent)'        },
  warning:  { bg: 'var(--color-warning-dim)',  color: 'var(--color-warning)' },
  info:     { bg: 'var(--color-info-dim)',     color: 'var(--color-info)'    },
};
const SERVICE_LABELS = ['sarvam','coaching','filestore','datastore','process','express'];

function ErrorsTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [serviceFilter, setServiceFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadErrors();
  }, []);

  const loadErrors = async (params = {}) => {
    setLoading(true); setMessage(null);
    try {
      const res = await getAdminErrors({ limit: 100, ...params });
      setEvents(res?.events || []);
    } catch (err) { setMessage({ type: 'error', text: err.message || 'Failed to load' }); }
    finally { setLoading(false); }
  };

  const handleFilter = () => { fetchedRef.current = true; loadErrors({ service: serviceFilter || undefined, severity: severityFilter || undefined }); };

  const handleClearAll = () => {
    setPendingConfirm({
      message: 'Delete all error events?',
      confirmLabel: 'Delete all',
      variant: 'danger',
      onConfirm: async () => {
        setClearing(true); setMessage(null);
        try {
          const res = await clearAdminErrors();
          setEvents([]);
          setMessage({ type: 'success', text: `Cleared ${res?.deleted ?? 0} events.` });
        } catch (err) { setMessage({ type: 'error', text: err.message || 'Failed to clear' }); }
        finally { setClearing(false); }
      },
    });
  };

  const toggleExpand = (id) => setExpandedIds(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p style={S.sectionHd} className="mb-0">Processing Issues</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-faint)' }}>Speeches that couldn't be analyzed — review and retry</p>
        </div>
        <DangerBtn onClick={handleClearAll} disabled={clearing || events.length === 0}>
          {clearing ? 'Clearing…' : `Clear All (${events.length})`}
        </DangerBtn>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} className="px-3 py-1.5 text-xs rounded-lg">
          <option value="">All services</option>
          {SERVICE_LABELS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} className="px-3 py-1.5 text-xs rounded-lg">
          <option value="">All severities</option>
          {['critical','error','warning','info'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={handleFilter} className="btn-bouncy text-xs px-3 py-2">Filter</button>
        <button
          onClick={() => { setServiceFilter(''); setSeverityFilter(''); fetchedRef.current = true; loadErrors(); }}
          className="text-xs font-medium px-2 transition-colors"
          style={{ color: 'var(--text-faint)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}
        >
          Reset
        </button>
      </div>

      <MsgBanner msg={message} />

      {loading ? (
        <div className="text-sm py-8 text-center" style={S.muted}>Loading events…</div>
      ) : events.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ background: 'var(--bg-input)', border: '1px dashed var(--border-accent)' }}>
          <p className="text-sm" style={S.muted}>No error events.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map((ev) => {
            const isExpanded = expandedIds.has(ev.event_id);
            const sev = SEVERITY[ev.severity] || { bg: 'var(--bg-input)', color: 'var(--text-secondary)' };
            return (
              <div key={ev.event_id} className="p-4" style={S.card}>
                <div className="flex items-start gap-3">
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap mt-0.5" style={{ background: sev.bg, color: sev.color }}>
                    {ev.severity}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-xs px-1.5 py-0.5 rounded font-mono"
                        style={{ background: 'var(--color-info-dim)', color: 'var(--color-info)' }}
                      >
                        {ev.service}
                      </span>
                      <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{ev.code}</span>
                      <span className="text-xs ml-auto" style={S.dimmer}>
                        {ev.created_at ? new Date(Number(ev.created_at)).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </span>
                    </div>
                    <p className="text-sm mt-1 break-words" style={{ color: 'var(--text-secondary)' }}>{ev.message}</p>
                    {ev.context && Object.keys(ev.context).length > 0 && (
                      <>
                        <button
                          onClick={() => toggleExpand(ev.event_id)}
                          className="text-xs mt-1 transition-colors"
                          style={{ color: 'var(--color-info)' }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--color-info)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--color-info)'}
                        >
                          {isExpanded ? '▲ Hide context' : '▼ Show context'}
                        </button>
                        {isExpanded && (
                          <pre
                            className="mt-2 text-xs p-3 rounded-lg overflow-x-auto"
                            style={{ background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
                          >
                            {JSON.stringify(ev.context, null, 2)}
                          </pre>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pendingConfirm && (
        <ConfirmDialog
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          variant={pendingConfirm.variant}
          onConfirm={() => { pendingConfirm.onConfirm(); setPendingConfirm(null); }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// App Feedback Tab
// ══════════════════════════════════════════════════════════════════════════

function FeedbackCard({ fb }) {
  const date = new Date(Number(fb.created_at));
  const formatted = isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="p-4 space-y-2" style={S.card}>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>{fb.content}</p>
      <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: '8px' }}>
        {fb.is_anonymous === true || fb.is_anonymous === 'true' ? (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full" style={{ background: 'var(--color-info-dim)', color: 'var(--color-info)', border: '1px solid var(--color-info-border)' }}>
            Anonymous
          </span>
        ) : (
          <>
            <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
              {fb.member_name || '—'}
            </span>
            {fb.member_email && (
              <span className="text-xs break-all" style={{ color: 'var(--text-muted)' }}>
                {fb.member_email}
              </span>
            )}
          </>
        )}
        <span className="ml-auto text-xs" style={{ color: 'var(--text-faint)' }}>
          {formatted}
        </span>
      </div>
    </div>
  );
}

function AppFeedbackTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const res = await getAppFeedback(200);
        setItems(res?.feedback || []);
      } catch (err) {
        setMessage({ type: 'error', text: err.message || 'Failed to load feedback' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p style={S.sectionHd} className="mb-0">Member Feedback</p>
        {!loading && (
          <span className="text-xs" style={{ color: 'var(--text-faint)' }}>
            {items.length} submission{items.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <MsgBanner msg={message} />

      {loading ? (
        <div className="text-sm py-8 text-center" style={S.muted}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ background: 'var(--bg-input)', border: '1px dashed var(--border-accent)' }}>
          <p className="text-sm" style={S.muted}>No feedback yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((fb) => <FeedbackCard key={fb.feedback_id} fb={fb} />)}
        </div>
      )}
    </div>
  );
}
